import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Search,
  Heart,
  Plus,
  MessageCircle,
  User,
  Home,
  TrendingUp,
  Users,
  Calendar,
  Bell,
  Settings,
  PlusCircle,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const MobileTabBar = () => {
  const { user, profile, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sellOpen, setSellOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [gateTabLabel, setGateTabLabel] = useState<string>("");

  const openLoginGate = (label: string) => {
    setGateTabLabel(label);
    setGateOpen(true);
  };

  const accountPath = !user
    ? "/auth"
    : profile?.role === "seller"
      ? "/seller-dashboard"
      : profile?.role === "professional"
        ? "/professional-dashboard"
        : "/buyer-dashboard";

  const savedPath = "/saved-homes";
  const inquiriesTab =
    profile?.role === "seller" || profile?.role === "professional" ? "inquiries" : "chats";
  const chatsPath = `${accountPath}?tab=${inquiriesTab}`;

  const role = profile?.role;
  const dashboardBase =
    role === "seller"
      ? "/seller-dashboard"
      : role === "professional"
        ? "/professional-dashboard"
        : "/buyer-dashboard";

  const sellerLinks = [
    { label: "Overview", icon: Home, to: `${dashboardBase}?tab=overview` },
    { label: "My Listings", icon: TrendingUp, to: `${dashboardBase}?tab=listings` },
    { label: "Saved", icon: Heart, to: `${dashboardBase}?tab=saved` },
    { label: "Inquiries", icon: Users, to: `${dashboardBase}?tab=inquiries` },
    { label: "Appointments", icon: Calendar, to: `${dashboardBase}?tab=appointments` },
    { label: "Notifications", icon: Bell, to: `${dashboardBase}?tab=notifications` },
    { label: "Settings", icon: Settings, to: `${dashboardBase}?tab=settings` },
  ];

  const dashboardTitle =
    role === "seller"
      ? "Seller Dashboard"
      : role === "professional"
        ? "Professional Dashboard"
        : "Buyer Dashboard";

  const handleDashboardClick = () => {
    if (authLoading) return;
    if (!user) {
      openLoginGate("Dashboard");
      return;
    }
    setSellOpen(true);
  };


  const tabs: Array<{
    label: string;
    icon: typeof Search;
    to?: string;
    primary?: boolean;
    onClick?: () => void;
    match: (p: string) => boolean;
  }> = [
    { to: "/listings", label: "Search", icon: Search, match: (p) => p.startsWith("/listings") },
    {
      label: "Saved",
      icon: Heart,
      onClick: () => {
        if (authLoading) return;
        if (!user) return openLoginGate("Saved");
        navigate(savedPath);
      },
      match: (p) => p.startsWith("/saved-homes") || p.includes("tab=saved"),
    },
    {
      label: "Dashboard",
      icon: Plus,
      primary: true,
      onClick: handleDashboardClick,
      match: () => false,
    },
    {
      label: "Chats",
      icon: MessageCircle,
      onClick: () => {
        if (authLoading) return;
        if (!user) return openLoginGate("Chats");
        navigate(chatsPath);
      },
      match: (p) => p.includes("tab=inquiries") || p.includes("tab=chats"),
    },
    {
      label: "Account",
      icon: User,
      onClick: () => {
        if (authLoading) return;
        if (!user) return openLoginGate("Account");
        navigate(`${accountPath}?tab=overview`);
      },
      match: (p) => p === accountPath || p.includes("tab=overview"),
    },
  ];

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-canvas/95 backdrop-blur-xl border-t border-ink/10"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 17px)" }}
        aria-label="Primary mobile navigation"
      >
        <div className="grid grid-cols-5 h-16 px-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = tab.match(location.pathname + location.search);
            const className = cn(
              "flex flex-col items-center justify-center gap-1 text-[10px] font-medium tracking-wide transition-colors",
              active ? "text-gold-deep" : "text-ink-soft hover:text-ink",
            );
            const inner = (
              <>
                {tab.primary ? (
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gold text-ink shadow-soft -mt-3">
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </span>
                ) : (
                  <Icon className={cn("w-5 h-5", active && "stroke-[2.25]")} strokeWidth={1.75} />
                )}
                <span className={cn(tab.primary && "mt-0")}>{tab.label}</span>
              </>
            );

            if (tab.onClick) {
              return (
                <button key={tab.label} type="button" onClick={tab.onClick} className={className}>
                  {inner}
                </button>
              );
            }

            return (
              <NavLink key={tab.label} to={tab.to!} className={className}>
                {inner}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Sell sheet */}
      <Sheet open={sellOpen} onOpenChange={setSellOpen}>
        <SheetContent
          side="bottom"
          className="md:hidden rounded-t-2xl border-t border-ink/10 bg-canvas pb-[calc(env(safe-area-inset-bottom)+5rem)]"
        >
          <SheetHeader className="text-left">
            <SheetTitle>{dashboardTitle}</SheetTitle>
          </SheetHeader>

          <button
            type="button"
            onClick={() => {
              setSellOpen(false);
              navigate("/list-property");
            }}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-gold text-ink font-medium py-3 shadow-soft"
          >
            <PlusCircle className="w-5 h-5" />
            List a Property
          </button>

          <div className="mt-4 grid grid-cols-2 gap-2">
            {sellerLinks.map((link) => {
              const Icon = link.icon;
              return (
                <button
                  key={link.label}
                  type="button"
                  onClick={() => {
                    setSellOpen(false);
                    navigate(link.to);
                  }}
                  className="flex items-center gap-3 rounded-xl border border-ink/10 bg-white px-4 py-3 text-left text-sm font-medium text-ink hover:bg-brand-warm-50 transition-colors"
                >
                  <Icon className="w-4 h-4 text-gold-deep" />
                  {link.label}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Login / Subscribe gate */}
      <Dialog open={gateOpen} onOpenChange={setGateOpen}>
        <DialogContent className="md:hidden max-w-[92vw] rounded-2xl">
          <DialogHeader>
            <DialogTitle>Sign in required</DialogTitle>
            <DialogDescription>
              Please sign in to access {gateTabLabel}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => setGateOpen(false)}
              className="rounded-xl border border-ink/10 bg-white px-4 py-2 text-sm font-medium text-ink"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setGateOpen(false);
                navigate("/auth");
              }}
              className="rounded-xl bg-gold text-ink font-medium px-4 py-2 text-sm shadow-soft"
            >
              Sign in
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MobileTabBar;
